import React, { createContext, useContext, useState } from 'react';
import styles from './tabs.module.scss';

type TabsContextValue = {
	activeTab: string;
	setActiveTab: (value: string) => void;
};

const TabsContext = createContext<TabsContextValue | null>(null);

function useTabsContext() {
	const ctx = useContext(TabsContext);
	if (!ctx) throw new Error('Tabs components must be used within <Tabs>');
	return ctx;
}

type TabsProps<T extends string> = {
	defaultValue?: T;
	value?: T;
	onValueChange?: (value: T) => void;
	className?: string;
	children: React.ReactNode;
};

function Tabs<T extends string>({
	defaultValue = '' as T,
	value,
	onValueChange,
	className,
	children,
}: TabsProps<T>) {
	const [internalValue, setInternalValue] = useState<T>(defaultValue);
	const activeTab = value !== undefined ? value : internalValue;

	const setActiveTab = (newValue: string) => {
		if (value === undefined) setInternalValue(newValue as T);
		onValueChange?.(newValue as T);
	};

	return (
		<TabsContext.Provider value={{ activeTab, setActiveTab }}>
			<div
				className={[styles['tabs'], className].filter(Boolean).join(' ')}
			>
				{children}
			</div>
		</TabsContext.Provider>
	);
}

type TabsListProps = {
	className?: string;
	children: React.ReactNode;
};

function TabsList({ className, children }: TabsListProps) {
	return (
		<div
			role="tablist"
			className={[styles['tabs-list'], className]
				.filter(Boolean)
				.join(' ')}
		>
			{children}
		</div>
	);
}

type TabsTriggerProps = {
	value: string;
	className?: string;
	children: React.ReactNode;
	disabled?: boolean;
	onClick?: React.MouseEventHandler<HTMLButtonElement>;
};

function TabsTrigger({
	value,
	className,
	children,
	disabled,
	onClick,
}: TabsTriggerProps) {
	const { activeTab, setActiveTab } = useTabsContext();
	const isActive = activeTab === value;

	const handleClick: React.MouseEventHandler<HTMLButtonElement> = (e) => {
		setActiveTab(value);
		onClick?.(e);
	};

	return (
		<button
			role="tab"
			type="button"
			aria-selected={isActive}
			disabled={disabled}
			data-active={isActive ? '' : undefined}
			className={[
				styles['tabs-trigger'],
				isActive ? styles['tabs-trigger--active'] : '',
				className,
			]
				.filter(Boolean)
				.join(' ')}
			onClick={handleClick}
		>
			{children}
		</button>
	);
}

type TabsContentProps = {
	value: string;
	className?: string;
	children: React.ReactNode;
};

function TabsContent({ value, className, children }: TabsContentProps) {
	const { activeTab } = useTabsContext();
	if (activeTab !== value) return null;

	return (
		<div
			role="tabpanel"
			className={[styles['tabs-content'], className]
				.filter(Boolean)
				.join(' ')}
		>
			{children}
		</div>
	);
}

export { Tabs, TabsList, TabsTrigger, TabsContent };
